import React, { useEffect, useState } from "react";
import { format, subDays } from "date-fns";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  AlertCircle,
  Calendar as CalendarIcon,
  Clock,
  Plus,
} from "lucide-react";
import { useAuthStore } from "@/store/authStore";
import { useStoreManagementStore } from "@/store/storeManagementStore";
import { StoreSelector } from "@/components/menu/StoreSelector";
import { AttendanceClockCard } from "@/components/attendance/AttendanceClockCard";
import { AbsenceForm } from "@/components/attendance/AbsenceForm";
import { AttendanceHistoryTable } from "@/components/attendance/AttendanceHistoryTable";
import { MonthlySummary } from "@/components/attendance/MonthlySummary";
import { ShiftScheduleForm } from "@/components/attendance/ShiftScheduleForm";
import { ShiftScheduleTable } from "@/components/attendance/ShiftScheduleTable";
import { useAttendanceStore } from "@/store/attendanceStore";
import {
  StaffAttendance as StaffAttendanceType,
  StaffShift,
} from "@/models/attendanceModel";
import { useTranslation } from "react-i18next";
import { StaffSelector } from "@/components/attendance/StaffSelector";
import { StoreEmployee } from "@/models/storeModel";

const StaffAttendance = () => {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const { stores, selectedStoreId } = useStoreManagementStore();
  const [selectedStaffId, setSelectedStaffId] = useState<string>("");
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [absenceSheetOpen, setAbsenceSheetOpen] = useState(false);
  const [shiftSheetOpen, setShiftSheetOpen] = useState(false);
  const [selectedShift, setSelectedShift] = useState<StaffShift | undefined>(
    undefined
  );
  const [employees, setEmployees] = useState<StoreEmployee[]>([]);

  const {
    attendanceHistory,
    fetchAttendanceHistory,
    fetchMonthlyAttendance,
    monthlyAttendance,
    shifts,
    fetchShifts,
    createAbsenceRecord,
    createOrUpdateShift,
    deleteShift,
  } = useAttendanceStore();

  const isStaffRole = user?.role === "staff";

  useEffect(() => {
    // Fetch stores
    useStoreManagementStore.getState().fetchStores();
  }, []);

  // When store is selected, fetch staff data
  useEffect(() => {
    if (selectedStoreId) {
      const fetchStaffData = async () => {
        try {
          const employees = await useStoreManagementStore
            .getState()
            .fetchEmployees(selectedStoreId);

          setEmployees(employees);

          // For staff users, automatically select themselves
          if (user?.role === "staff" && user.id) {
            const staffMember = employees.find((emp) => emp.userId === user.id);
            if (staffMember) {
              setSelectedStaffId(staffMember.id);
            }
          }
          // For managers, select the first staff member if any
          else if (employees.length > 0) {
            setSelectedStaffId(employees[0].id);
          }
        } catch (error) {
          console.error("Error fetching staff:", error);
        }
      };

      fetchStaffData();
    }
  }, [selectedStoreId, user]);

  // Fetch attendance data when staff is selected
  useEffect(() => {
    if (selectedStaffId && selectedStoreId) {
      // Get attendance history for the last 30 days
      fetchAttendanceHistory(
        selectedStaffId,
        selectedStoreId,
        subDays(new Date(), 30)
      );

      // Get monthly attendance data
      const now = new Date();
      fetchMonthlyAttendance(
        selectedStaffId,
        now.getFullYear(),
        now.getMonth() + 1
      );

      // Get shift schedules
      fetchShifts(selectedStaffId, selectedStoreId);
    }
  }, [
    selectedStaffId,
    selectedStoreId,
    fetchAttendanceHistory,
    fetchMonthlyAttendance,
    fetchShifts,
  ]);

  const handleAbsenceSubmit = async (data: {
    type: "holiday" | "sick" | "other";
    startDate: Date;
    endDate: Date;
    description: string;
  }) => {
    await createAbsenceRecord(
      selectedStaffId,
      selectedStoreId,
      data.type,
      data.startDate,
      data.endDate,
      data.description
    );
    setAbsenceSheetOpen(false);
  };

  const handleShiftSubmit = async (
    shift: Omit<StaffShift, "id" | "created_at" | "updated_at">
  ) => {
    await createOrUpdateShift(shift);
    setShiftSheetOpen(false);
    setSelectedShift(undefined);
  };

  const handleEditShift = (shift: StaffShift) => {
    setSelectedShift(shift);
    setShiftSheetOpen(true);
  };

  const handleDeleteShift = async (shiftId: string) => {
    if (confirm("Are you sure you want to delete this shift?")) {
      await deleteShift(shiftId);
    }
  };

  // Determine if store selection should be disabled
  const isStoreSelectionDisabled = user?.role === "staff";

  // Format the month for display
  const formattedMonth = format(selectedDate, "MMMM yyyy");

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            {t("staffAttendance")}
          </h1>
          <p className="text-muted-foreground mt-1">
            {t("manageStaffAttendance")}
          </p>
        </div>
        <div className="flex items-center gap-4">
          {selectedStoreId && (
            <StaffSelector
              employees={employees}
              selectedStaffId={selectedStaffId}
              onStaffSelect={setSelectedStaffId}
              disabled={user?.role === "staff"}
            />
          )}
        </div>
      </div>

      {selectedStaffId && selectedStoreId ? (
        <div className="grid gap-6 md:grid-cols-6">
          <div className="md:col-span-2 space-y-6">
            {isStaffRole ? (
              <AttendanceClockCard
                storeId={selectedStoreId}
                staffId={selectedStaffId}
              />
            ) : (
              <Card>
                <CardHeader>
                  <CardTitle>{t("attendanceView")}</CardTitle>
                  <CardDescription>{t("viewOnlyAccess")}</CardDescription>
                </CardHeader>
              </Card>
            )}

            {monthlyAttendance && (
              <MonthlySummary
                totalHours={monthlyAttendance.totalHours}
                byType={monthlyAttendance.byType}
              />
            )}

            {isStaffRole && (
              <Card>
                <CardHeader className="pb-3">
                  <div className="flex justify-between items-center">
                    <CardTitle className="text-lg">
                      {t("actionCenter")}
                    </CardTitle>
                  </div>
                </CardHeader>
                <CardContent className="space-y-2">
                  <Sheet
                    open={absenceSheetOpen}
                    onOpenChange={setAbsenceSheetOpen}
                  >
                    <SheetTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full flex items-center justify-start"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {t("recordAbsence")}
                      </Button>
                    </SheetTrigger>
                    <SheetContent>
                      <SheetHeader>
                        <SheetTitle>{t("recordAbsence")}</SheetTitle>
                        <SheetDescription>
                          {t("submitAbsence")}
                        </SheetDescription>
                      </SheetHeader>
                      <div className="mt-6">
                        <AbsenceForm
                          storeId={selectedStoreId}
                          staffId={selectedStaffId}
                          onSubmit={handleAbsenceSubmit}
                          onCancel={() => setAbsenceSheetOpen(false)}
                        />
                      </div>
                    </SheetContent>
                  </Sheet>

                  <Sheet open={shiftSheetOpen} onOpenChange={setShiftSheetOpen}>
                    <SheetTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full flex items-center justify-start"
                      >
                        <Clock className="mr-2 h-4 w-4" />
                        {t("manageShiftSchedule")}
                      </Button>
                    </SheetTrigger>
                    <SheetContent>
                      <SheetHeader>
                        <SheetTitle>
                          {selectedShift ? t("updateShift") : t("addNewShift")}
                        </SheetTitle>
                        <SheetDescription>
                          {t("setYourRegularHours")}
                        </SheetDescription>
                      </SheetHeader>
                      <div className="mt-6">
                        <ShiftScheduleForm
                          storeId={selectedStoreId}
                          staffId={selectedStaffId}
                          existingShift={selectedShift}
                          onSubmit={handleShiftSubmit}
                          onCancel={() => {
                            setShiftSheetOpen(false);
                            setSelectedShift(undefined);
                          }}
                        />
                      </div>
                    </SheetContent>
                  </Sheet>
                </CardContent>
              </Card>
            )}
          </div>

          <div className="md:col-span-4">
            <Card>
              <Tabs defaultValue="history">
                <CardHeader className="pb-0">
                  <div className="flex items-center justify-between">
                    <TabsList>
                      <TabsTrigger value="history">
                        {t("attendanceHistory")}
                      </TabsTrigger>
                      <TabsTrigger value="leaves">
                        {t("leavesAndAbsences")}
                      </TabsTrigger>
                      <TabsTrigger value="schedule">
                        {t("shiftSchedule")}
                      </TabsTrigger>
                    </TabsList>
                  </div>
                </CardHeader>
                <CardContent className="pt-6">
                  <TabsContent value="history">
                    <AttendanceHistoryTable
                      records={attendanceHistory.filter(
                        (record) =>
                          record.type === "work" || record.type === "overtime"
                      )}
                    />
                  </TabsContent>
                  <TabsContent value="leaves">
                    <AttendanceHistoryTable
                      records={attendanceHistory.filter(
                        (record) =>
                          record.type === "holiday" ||
                          record.type === "sick" ||
                          record.type === "other"
                      )}
                    />
                  </TabsContent>
                  <TabsContent value="schedule">
                    <div className="space-y-4">
                      <div className="flex justify-between items-center">
                        <h3 className="font-semibold">{t("weeklySchedule")}</h3>
                        <Button
                          size="sm"
                          onClick={() => {
                            setSelectedShift(undefined);
                            setShiftSheetOpen(true);
                          }}
                        >
                          <Plus className="h-4 w-4 mr-1" /> {t("addShift")}
                        </Button>
                      </div>
                      <ShiftScheduleTable
                        shifts={shifts}
                        onEdit={handleEditShift}
                        onDelete={handleDeleteShift}
                      />
                    </div>
                  </TabsContent>
                </CardContent>
              </Tabs>
            </Card>
          </div>
        </div>
      ) : (
        <Card className="border-dashed">
          <CardHeader className="pb-0">
            <CardTitle>{t("selectStoreAndStaff")}</CardTitle>
            <CardDescription>{t("selectStoreDescription")}</CardDescription>
          </CardHeader>
          <CardContent className="py-6">
            <div className="flex flex-col items-center justify-center text-center p-6 space-y-4">
              <AlertCircle className="h-12 w-12 text-muted-foreground/80" />
              <h3 className="font-semibold text-lg">{t("noDataSelected")}</h3>
              <p className="text-sm text-muted-foreground max-w-md">
                {t("noDataDescription")}
              </p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default StaffAttendance;
